// Fluid therapy reference values & calculations.
// Source: 2024 AAHA Fluid Therapy Guidelines for Dogs and Cats; Plumb's Veterinary Drug Handbook.

export const FLUID_TYPES = [
  { id: "LRS", name: "Lactated Ringer's (Hartmann's)", tonicity: "Isotonic crystalloid" },
  { id: "NaCl09", name: "0.9% Sodium Chloride", tonicity: "Isotonic crystalloid" },
  { id: "Plasmalyte", name: "Plasma-Lyte 148", tonicity: "Buffered isotonic crystalloid" },
  { id: "Norm-R", name: "Normosol-R", tonicity: "Buffered isotonic crystalloid" },
];

// Intra-operative / anaesthesia starting crystalloid rates (mL/kg/hr) — 2024 AAHA
export const SURGICAL_RATE = { dog: 5, cat: 3 };
// Display labels for the reference table (cats: 3–5 mL/kg/hr per 2024 AAHA)
export const SURGICAL_RATE_LABEL = { dog: "5", cat: "3–5" };
// Conscious maintenance rates (mL/kg/hr) ≈ 60 mL/kg/day (dog) / 40 mL/kg/day (cat) — 2024 AAHA
export const MAINTENANCE_RATE = { dog: 2.5, cat: 1.7 };
export const MAINTENANCE_DAILY = { dog: 60, cat: 40 };
// Hypovolaemic shock boluses (mL/kg) over 15–30 min, repeated to effect — 2024 AAHA
export const SHOCK_BOLUS = { dog: "15–20", cat: "5–10" };

export const round1 = (n) => Math.round(n * 10) / 10;

export function speciesKey(patient) {
  return patient?.species === "cat" ? "cat" : "dog";
}

// Returns the full fluid plan derived from the patient's weight & species.
export function computeFluidPlan(patient) {
  const w = parseFloat(patient?.weightKg) || 0;
  const sp = speciesKey(patient);
  const surgicalRate = round1(w * SURGICAL_RATE[sp]); // mL/h
  const maintenanceRate = round1(w * MAINTENANCE_RATE[sp]); // mL/h

  const surgeryHours = 1.5;
  const maintenanceHours = 6;

  return {
    w,
    sp,
    surgicalRate,
    maintenanceRate,
    perKg: { surgery: SURGICAL_RATE[sp], maintenance: MAINTENANCE_RATE[sp] },
    orders: {
      surgery: {
        id: "surgery",
        label: "Intra-operative",
        rate: surgicalRate,
        durationHr: surgeryHours,
        vtbi: Math.round(surgicalRate * surgeryHours),
      },
      maintenance: {
        id: "maintenance",
        label: "Maintenance",
        rate: maintenanceRate,
        durationHr: maintenanceHours,
        vtbi: Math.round(maintenanceRate * maintenanceHours),
      },
    },
  };
}

const VET_NAMES = ["Dr. Alvarez", "Dr. Chen", "Dr. Okafor", "Dr. Patel"];

// Human-readable clinical instruction for a given order mode.
// Includes the patient's weight so the vet's instruction is self-contained.
export function buildOrderText(plan, mode) {
  const vet = VET_NAMES[Math.round(plan.w) % VET_NAMES.length];
  const animal = `${plan.w} kg ${plan.sp === "cat" ? "cat" : "dog"}`;
  const order = plan.orders[mode];
  if (mode === "surgery") {
    return {
      vet,
      heading: "Intra-operative fluid order",
      body: `This ${animal} is going to theatre. Start Lactated Ringer's at the intra-operative anaesthesia rate of ${plan.perKg.surgery} mL/kg/hr. Set the pump to run for the estimated ${order.durationHr} hr surgery.`,
      hint: `Rate = weight (${plan.w} kg) × ${plan.perKg.surgery} mL/kg/hr · VTBI = rate × ${order.durationHr} hr`,
    };
  }
  return {
    vet,
    heading: "Maintenance fluid order",
    body: `This ${animal} is recovering in the ward. Continue Lactated Ringer's at the maintenance rate of ${plan.perKg.maintenance} mL/kg/hr for the next ${order.durationHr} hr.`,
    hint: `Rate = weight (${plan.w} kg) × ${plan.perKg.maintenance} mL/kg/hr · VTBI = rate × ${order.durationHr} hr`,
  };
}
